import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import YmmAutocompleteField from "../../YmmAutocompleteField";
import { borderRadius, useTheme } from "@mui/system";
import { CheckCircle } from "@mui/icons-material";
import { ArtifactStyle } from "./VerficationArtifactsStyles";
import { useVerification } from "../../../../../context/Verification.context";
import { useAuth } from "../../../../../context/Auth.context";
import { capitalizeFirstLetter } from "../../../../../utils/Utils";
import { useQuery } from "@apollo/client";
import { FETCH_YMMT, GET_DEALERSHIPS } from "../../../../../graphql/query/query";

const VehicleTransactionResponse = ({
  artifact,
  isPopup = false,
  handleNext,
  handleClose,
  setVehicleTransactionScreen,
  onSubmit,
  onInteraction,
}) => {
  const theme = useTheme();
  const verification = useVerification();
  const auth = useAuth();
  const isSpanish = verification?.aiData?.spanish;
  const { vehicleDetail, setVehicleDetail } = verification;

  const [selectedVehicle, setSelectedVehicle] = useState(
    verification.artifactsResponse?.vehicleType || "",
  );
  const [selectedTransaction, setSelectedTransaction] = useState(
    verification.artifactsResponse?.transactionType || "",
  );

  const [saveYear, setSaveYear] = useState(null);
  const [saveMake, setSaveMake] = useState(null);
  const [saveModel, setSaveModel] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // ── shared IDs ──────────────────────────────────────────────────────────────
  const sharedIds = {
    user_id: parseInt(verification?.customer?.userId),
    employee_user_id: parseInt(verification?.employeeId),
    dealership_id: parseInt(verification?.dealershipId),
    artifact_id: artifact?.id ?? null,
  };

  // ── dealer brand (pinned first in Make dropdown) ─────────────────────────────
  // brand_name lives on the dealership record, not the auth/session user, and
  // can change after login — so we fetch it live by dealership_id rather than
  // reading it off `auth.user`.
  // NOTE: assumes FilterInput supports { dealership_id }. Confirm against
  // wherever GET_DEALERSHIPS is already called in the admin/list screens —
  // adjust the variables shape below if it differs.
  const filterDate = React.useMemo(() => new Date().toISOString(), []);
const { data: dealershipData, loading: dealershipLoading, error: dealershipError } = useQuery(GET_DEALERSHIPS, {
  variables: {
    filterInput: {
      dealership_id: parseInt(verification?.dealershipId),
       date: filterDate,
    },
  },
  skip: !verification?.dealershipId,
  fetchPolicy: "cache-first",
});

console.log("dealershipLoading:", dealershipLoading);
console.log("dealershipError:", dealershipError);
console.log("dealershipData:", dealershipData);


const toTitleCase = (str) =>
  str
    .toLowerCase()
    .replace(/(^|[\s-])(\S)/g, (_, sep, ch) => sep + ch.toUpperCase());

const dealerName = React.useMemo(() => {
  const raw = dealershipData?.getDealerships?.dealerships?.[0]?.brand_name;
  return raw?.trim() ? toTitleCase(raw.trim()) : "";
}, [dealershipData]);

  // ── DEBUG: remove once confirmed ─────────────────────────────────────────
  console.log("dealershipData:", dealershipData);
  console.log("dealerName:", dealerName);
  // ──────────────────────────────────────────────────────────────────────────

  // Prepend dealer brand to the Make options list (de-duped, brand first).
  // With no brand, this returns the plain makes list.
  const makeOptionsWithDealer = React.useMemo(() => {
    const base = saveMake || [];
    if (!dealerName) return base;
    const rest = base.filter(
      (m) => m?.toLowerCase() !== dealerName.toLowerCase(),
    );
    return [dealerName, ...rest];
  }, [saveMake, dealerName]);

  // ── DEBUG: remove once confirmed ─────────────────────────────────────────
  console.log("makeOptionsWithDealer:", makeOptionsWithDealer);
  console.log("verification?.dealershipId:", verification?.dealershipId);
  // ──────────────────────────────────────────────────────────────────────────

  const { data: yearData } = useQuery(FETCH_YMMT, {
    variables: { ymmtInput: { year: "", make: "", model: "" } },
  });

  const { data: fetchOutput, refetch } = useQuery(FETCH_YMMT, {
    variables: {
      ymmtInput: {
        year: vehicleDetail?.year ?? "",
        make: vehicleDetail?.make ?? "",
        model: vehicleDetail?.model ?? "",
      },
    },
  });

  const autocompletePaperSx = {
    minWidth: {
      xs: 125,
      sm: "auto",
    },
  };

  useEffect(() => {
    if (yearData?.fetchYmmt?.yearArray) {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;
      const years = yearData.fetchYmmt.yearArray
        .map(Number)
        .filter((y) => y !== 9999 && y <= nextYear)
        .sort((a, b) => b - a);
      setSaveYear(years.map(String));
    }
  }, [yearData]);

  // As soon as we have a Year and YMMT data, always populate Make options.
  // Model options are populated only once a Make is present.
  useEffect(() => {
    if (fetchOutput?.fetchYmmt && vehicleDetail?.year) {
      const { makeArray, modelArray } = fetchOutput.fetchYmmt;

      setSaveMake(makeArray || []);

      if (vehicleDetail?.make) {
        setSaveModel(modelArray || []);
      } else {
        setSaveModel([]);
      }
    }
  }, [fetchOutput, vehicleDetail?.year, vehicleDetail?.make]);

  const parseJSON = (str) => {
    if (!str) return {};
    if (typeof str === "object") return str;
    try {
      return JSON.parse(str);
    } catch (e) {
      return {};
    }
  };

  const vehicleOptions = parseJSON(
    isSpanish ? artifact.es_vehicle_options : artifact.vehicle_options,
  );
  const transactionOptions = parseJSON(
    isSpanish ? artifact.es_transaction_options : artifact.transaction_options,
  );

  // ── helpers ───────────────────────────────────────────────────────────────

  const VEHICLE_KEYS = ["N", "U", "CPO", "O"];
  const TRANSACTION_KEYS = ["F", "L", "C", "B"];

  // Vehicle Type options (used for both display purposes and to drive the
  // Warranty Module's coverage chart — GAS / HYBRID / EV / DIESEL).
  const ENGINE_OPTIONS = isSpanish
    ? [
        { key: "GAS", label: "Gasolina" },
        { key: "EV", label: "Eléctrico" },
        { key: "DIESEL", label: "Diésel" },
        { key: "HYBRID", label: "Híbrido" },
      ]
    : [
        { key: "GAS", label: "Gas" },
        { key: "EV", label: "EV" },
        { key: "DIESEL", label: "Diesel" },
        { key: "HYBRID", label: "Hybrid" },
      ];

  const resolveKey = (keyMap, optionKey) => {
    const idx = parseInt(optionKey.replace("option", "")) - 1;
    return keyMap[idx] ?? null;
  };

  const emptyToNull = (val) => (val === "" || val === undefined ? null : val);

  // ── handlers ─────────────────────────────────────────────────────────────────

  const handleVehicleSelect = (option, key) => {
    if (!key) return;
    onInteraction?.();
    setSelectedVehicle(key);
    verification.setArtifactsResponse((prev) => ({
      ...prev,
      vehicleType: key,
      vehicleTransactionResponse: {
        ...sharedIds,
        ...(prev?.vehicleTransactionResponse || {}),
        vehicle_type: key,
      },
    }));
  };

  const handleTransactionSelect = (option, key) => {
    if (!key) return;
    onInteraction?.();
    setSelectedTransaction(key);
    verification.setArtifactsResponse((prev) => ({
      ...prev,
      transactionType: key,
      vehicleTransactionResponse: {
        ...sharedIds,
        ...(prev?.vehicleTransactionResponse || {}),
        transaction_type: key,
      },
    }));
  };

  // Vehicle Type (engine) selection — also feeds the Warranty Module's
  // coverage chart via verification.artifactsResponse.vehicleTransactionResponse.engine
  const handleEngineSelect = (newValue) => {
    setVehicleDetail({ ...vehicleDetail, engine: newValue });
    verification.setArtifactsResponse((prev) => ({
      ...prev,
      vehicleTransactionResponse: {
        ...sharedIds,
        ...(prev?.vehicleTransactionResponse || {}),
        engine: emptyToNull(newValue),
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      await onSubmit();
      setIsSubmitted(true);
    } catch (e) {
      console.error(e);
    }
  };

  // ── styles ────────────────────────────────────────────────────────────────────

  const sharedBtnSx = {
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "8px",
    display: "block",
    padding: isPopup
      ? { xs: "4px 6px", md: "4px 8px" }
      : { xs: "4px", md: "2px 8px" },
    minHeight: isPopup
      ? { xs: "40px", md: "36px" }
      : { xs: "45px", md: "36px" },
    mx: { xs: "auto", md: "0" },
    width: "100%",
    minWidth: 0,
    height: "auto",
    fontSize: isPopup ? { xs: "12px", sm: "13px" } : "13px",
    lineHeight: 1.15,
    whiteSpace: "normal",
    wordBreak: "break-word",
  };
  const popupSx = {
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: {
      xs: "94%",
      sm: "90%",
      md: "88%",
    },
    maxWidth: "640px",
    boxSizing: "border-box",
    alignItems: "stretch",
    maxHeight: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    px: { xs: 1.5, sm: 2 },
    py: 1.5,
    backgroundColor: "rgba(255,255,255,0.95)",
    border: "1px solid black",
  };

  const textSx = {
    ...ArtifactStyle.artifactBox,
    width: "100%",
    alignItems: "flex-start",
    overflowY: "auto",
  };

  return (
    <Box sx={isPopup ? popupSx : textSx}>
      {/* Intro */}
      {!isPopup && (
        <Typography sx={{ fontSize: "16px" }}>
          {isSpanish
            ? "Para personalizar tu experiencia, responde a las siguientes preguntas:"
            : "Please answer the following questions to help customize your experience:"}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isPopup ? 1.5 : 3,
          mt: { xs: isPopup ? 0 : 2, sm: 0, md: 0 },
          mb: isPopup ? 0 : 2,
          width: "100%",
        }}
      >
        {/* Q1 — Vehicle Type (New/Used/CPO/Other) */}
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Typography
            sx={{
              mb: 1,
              fontSize: isPopup ? { xs: "13px", sm: "14px" } : undefined,
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {isSpanish ? artifact.es_vehicle_message : artifact.vehicle_message}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isPopup
                ? "1fr 1fr"
                : { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" },
              gap: 1.5,
              width: "100%",
            }}
          >
            {Object.entries(vehicleOptions || {}).map(([key, value]) => {
              const internalKey = resolveKey(VEHICLE_KEYS, key);
              if (!internalKey) return null;
              return (
                <Button
                  key={key}
                  size="small"
                  variant={
                    selectedVehicle === internalKey ? "contained" : "outlined"
                  }
                  onClick={() => handleVehicleSelect(value, internalKey)}
                  sx={sharedBtnSx}
                >
                  {value}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Q2 — Transaction Type */}
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Typography
            sx={{
              mb: 1,
              fontSize: isPopup ? { xs: "13px", sm: "14px" } : undefined,
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {isSpanish
              ? artifact.es_transaction_message
              : artifact.transaction_message}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isPopup
                ? "1fr 1fr"
                : { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" },
              gap: 1.5,
              width: "100%",
            }}
          >
            {Object.entries(transactionOptions || {}).map(([key, value]) => {
              const internalKey = resolveKey(TRANSACTION_KEYS, key);
              if (!internalKey) return null;
              return (
                <Button
                  key={key}
                  size="small"
                  variant={
                    selectedTransaction === internalKey
                      ? "contained"
                      : "outlined"
                  }
                  onClick={() => handleTransactionSelect(value, internalKey)}
                  sx={sharedBtnSx}
                >
                  {value}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Q3 — YMM + Vehicle Type (Engine: Gas/EV/Diesel/Hybrid) */}
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Typography
            sx={{
              mb: 1,
              fontSize: isPopup ? { xs: "13px", sm: "14px" } : undefined,
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {isSpanish
              ? "¿Qué vehículo compró hoy?"
              : "What vehicle did you purchase today?"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: {
                xs: 1,
                sm: isPopup ? 1.5 : 2.5,
                md: isPopup ? 1.5 : 2.5,
              },
              width: "100%",
              minWidth: 0,
            }}
          >
            {/* Year */}
            <FormControl
              size="small"
              sx={{
                flex: 1,
                minWidth: { xs: "45%", sm: 0 },
                maxWidth: isPopup
                  ? "100%"
                  : { xs: "220px", sm: "200px", md: "200px" },
              }}
            >
              <YmmAutocompleteField
                fieldType="year"
                size="small"
                options={saveYear || []}
                value={vehicleDetail?.year || ""}
                label={isSpanish ? "Año" : "Year"}
                isSpanish={isSpanish}
                textFieldSx={{ backgroundColor: "white" }}
                paperSx={autocompletePaperSx}
                onChange={(newValue) => {
                  setSaveMake([]);
                  setSaveModel([]);
                  setVehicleDetail({
                    ...vehicleDetail,
                    year: newValue,
                    make: "",
                    model: "",
                    engine: "",
                  });
                  verification.setArtifactsResponse((prev) => ({
                    ...prev,
                    vehicleTransactionResponse: {
                      ...sharedIds,
                      ...(prev?.vehicleTransactionResponse || {}),
                      year: emptyToNull(newValue),
                      make: null,
                      model: null,
                      engine: null,
                    },
                  }));
                }}
                onRefetch={(newValue) =>
                  refetch({
                    ymmtInput: { year: newValue, make: "", model: "" },
                  })
                }
              />
            </FormControl>

            {/* Make */}
            <FormControl
              size="small"
              sx={{
                flex: 1,
                minWidth: { xs: "45%", sm: 0 },
                maxWidth: isPopup
                  ? "100%"
                  : { xs: "220px", sm: "200px", md: "200px" },
              }}
            >
              <YmmAutocompleteField
                fieldType="make"
                size="small"
                options={makeOptionsWithDealer}
                value={vehicleDetail?.make || ""}
                label={isSpanish ? "Marca" : "Make"}
                isSpanish={isSpanish}
                disabled={!vehicleDetail?.year}
                textFieldSx={{ backgroundColor: "white" }}
                paperSx={autocompletePaperSx}
                onChange={(newValue) => {
                  setSaveModel([]);
                  setVehicleDetail({
                    ...vehicleDetail,
                    make: newValue,
                    model: "",
                    engine: "",
                  });
                  verification.setArtifactsResponse((prev) => ({
                    ...prev,
                    vehicleTransactionResponse: {
                      ...sharedIds,
                      ...(prev?.vehicleTransactionResponse || {}),
                      make: emptyToNull(newValue),
                      model: null,
                      engine: null,
                    },
                  }));
                }}
                onRefetch={(newValue) =>
                  refetch({
                    ymmtInput: {
                      year: vehicleDetail?.year ?? "",
                      make: newValue,
                      model: "",
                    },
                  })
                }
              />
            </FormControl>

            {/* Model */}
            <FormControl
              size="small"
              sx={{
                flex: 1,
                minWidth: { xs: "45%", sm: 0 },
                maxWidth: isPopup
                  ? "100%"
                  : { xs: "220px", sm: "200px", md: "200px" },
              }}
            >
              <YmmAutocompleteField
                fieldType="model"
                size="small"
                options={saveModel || []}
                value={vehicleDetail?.model || ""}
                label={isSpanish ? "Modelo" : "Model"}
                isSpanish={isSpanish}
                disabled={!vehicleDetail?.make || !vehicleDetail?.year}
                textFieldSx={{ backgroundColor: "white" }}
                paperSx={autocompletePaperSx}
                onChange={(newValue) => {
                  setVehicleDetail({ ...vehicleDetail, model: newValue });
                  verification.setArtifactsResponse((prev) => ({
                    ...prev,
                    vehicleTransactionResponse: {
                      ...sharedIds,
                      ...(prev?.vehicleTransactionResponse || {}),
                      model: emptyToNull(newValue),
                    },
                  }));
                }}
              />
            </FormControl>

            {/* Vehicle Type (Engine: Gas/EV/Diesel/Hybrid) */}
            <FormControl
              size="small"
              sx={{
                flex: 1,
                minWidth: { xs: "45%", sm: 0 },
                maxWidth: { xs: "220px", sm: "200px", md: "200px" },
              }}
            >
              <InputLabel id="engine-select-label">
                {isSpanish ? "Motor" : "Engine"}
              </InputLabel>
              <Select
                labelId="engine-select-label"
                value={vehicleDetail?.engine || ""}
                label={isSpanish ? "Motor" : "Engine"}
                onChange={(e) => handleEngineSelect(e.target.value)}
                sx={{
                  "& .MuiSelect-select": {
                    height: "23px", // matches MUI's small-outlined content height (40px total with padding)
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                {ENGINE_OPTIONS.map(({ key, label }) => (
                  <MenuItem
                    key={key}
                    value={key}
                    sx={{ fontSize: "17px", minHeight: 32 }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Submit / Success */}
      {/* {isPopup && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          {isSubmitted ? (
            <Box sx={{ display: "flex", my: "14px" }}>
              <CheckCircle color="primary" />
              <span style={{ color: theme.palette.primary.main, marginLeft: "4px" }}>
                {isSpanish ? "ÉXITO" : "SUCCESS"}
              </span>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ width: "50%", minWidth: "110px" }}
            >
              {isSpanish ? "Enviar" : "Submit"}
            </Button>
          )}
        </Box>
      )} */}
    </Box>
  );
};

export default VehicleTransactionResponse;